import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const IncidentSidebar = ({ 
  incidents = [], 
  selectedIncident, 
  onIncidentSelect, 
  isVisible = true, 
  onToggle,
  isMobile = false 
}) => {
  const [sortBy, setSortBy] = useState('severity');
  const [searchQuery, setSearchQuery] = useState('');

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-error bg-error/10';
      case 'major': return 'text-warning bg-warning/10';
      case 'minor': return 'text-accent bg-accent/10';
      case 'resolved': return 'text-success bg-success/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'internet': return 'Wifi';
      case 'mobile': return 'Smartphone';
      case 'streaming': return 'Play';
      case 'cloud': return 'Cloud';
      case 'financial': return 'CreditCard';
      case 'gaming': return 'Gamepad2';
      default: return 'AlertCircle';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'AlertTriangle';
      case 'resolved': return 'CheckCircle';
      case 'investigating': return 'Search';
      default: return 'Clock';
    }
  };

  const sortIncidents = (incidents) => {
    const sorted = [...incidents];
    switch (sortBy) {
      case 'severity':
        const severityOrder = { critical: 0, major: 1, minor: 2, resolved: 3 };
        return sorted?.sort((a, b) => severityOrder?.[a?.severity] - severityOrder?.[b?.severity]);
      case 'users':
        return sorted?.sort((a, b) => b?.affectedUsers - a?.affectedUsers);
      case 'duration':
        return sorted?.sort((a, b) => {
          const getDurationMinutes = (duration) => {
            const match = duration?.match(/(\d+)h\s*(\d+)m|(\d+)m/);
            if (!match) return 0;
            const hours = parseInt(match?.[1]) || 0;
            const minutes = parseInt(match?.[2]) || parseInt(match?.[3]) || 0;
            return hours * 60 + minutes;
          };
          return getDurationMinutes(b?.duration) - getDurationMinutes(a?.duration);
        });
      case 'recent':
        return sorted?.sort((a, b) => b?.id - a?.id);
      default:
        return sorted;
    }
  };

  const filterIncidents = (incidents) => {
    if (!searchQuery) return incidents;
    return incidents?.filter(incident =>
      incident?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      incident?.provider?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      incident?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    );
  };

  const processedIncidents = sortIncidents(filterIncidents(incidents));

  if (isMobile) {
    return (
      <div className={`fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border transform transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}>
        {/* Mobile Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-foreground">
              Incidents ({processedIncidents?.length})
            </h3>
          </div>
          <Button variant="ghost" size="sm" onClick={onToggle}>
            <Icon name="ChevronDown" size={20} />
          </Button>
        </div>
        {/* Mobile Content */}
        <div className="max-h-96 overflow-y-auto">
          {processedIncidents?.slice(0, 5)?.map((incident) => (
            <div
              key={incident?.id}
              className={`p-4 border-b border-border cursor-pointer hover:bg-muted/50 transition-colors ${
                selectedIncident?.id === incident?.id ? 'bg-primary/5 border-l-4 border-l-primary' : ''
              }`}
              onClick={() => onIncidentSelect(incident)}
            >
              <div className="flex items-start space-x-3">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${getSeverityColor(incident?.severity)}`}>
                  <Icon name={getServiceIcon(incident?.serviceType)} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {incident?.title}
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    {incident?.provider} • {incident?.affectedUsers?.toLocaleString()} users
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!isVisible) {
    return (
      <button
        onClick={onToggle}
        className="fixed left-4 top-1/2 -translate-y-1/2 z-40 bg-card border border-border rounded-r-lg p-3 shadow-lg hover:shadow-xl transition-shadow"
        title="Show Incident List"
      >
        <Icon name="ChevronRight" size={20} />
      </button>
    );
  }

  return (
    <div className="w-96 bg-card border-r border-border flex flex-col h-full">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            Active Incidents
          </h3>
          <Button variant="ghost" size="sm" onClick={onToggle}>
            <Icon name="ChevronLeft" size={20} />
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search incidents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Sort Options */}
        <div className="flex items-center space-x-2">
          <span className="text-xs text-muted-foreground">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="text-xs bg-background border border-border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="severity">Severity</option>
            <option value="users">Affected Users</option>
            <option value="duration">Duration</option>
            <option value="recent">Most Recent</option>
          </select>
        </div>
      </div>
      {/* Incident List */}
      <div className="flex-1 overflow-y-auto">
        {processedIncidents?.length === 0 ? (
          <div className="p-6 text-center">
            <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">
              {searchQuery ? 'No incidents match your search' : 'No incidents found'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {processedIncidents?.map((incident) => (
              <div
                key={incident?.id}
                className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors border-l-4 ${
                  selectedIncident?.id === incident?.id 
                    ? 'bg-primary/5 border-l-primary' :'border-l-transparent'
                }`}
                onClick={() => onIncidentSelect(incident)}
              >
                {/* Incident Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className={`flex items-center justify-center w-6 h-6 rounded-full ${getSeverityColor(incident?.severity)}`}>
                      <Icon name={getServiceIcon(incident?.serviceType)} size={12} />
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getSeverityColor(incident?.severity)}`}>
                      {incident?.severity?.toUpperCase()}
                    </span>
                  </div>
                  <Icon name={getStatusIcon(incident?.status)} size={14} className="text-muted-foreground" />
                </div>

                {/* Incident Details */}
                <h4 className="text-sm font-medium text-foreground mb-1 line-clamp-2">
                  {incident?.title}
                </h4>
                
                <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                  {incident?.description}
                </p>

                {/* Provider Info */}
                <div className="flex items-center space-x-2 mb-2">
                  <Image
                    src={`https://logo.clearbit.com/${incident?.provider?.toLowerCase()?.replace(/\s+/g, '')}.com`}
                    alt={`${incident?.provider} company logo`}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-xs font-medium text-foreground">
                    {incident?.provider}
                  </span>
                </div>

                {/* Metrics */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center space-x-3">
                    <span className="flex items-center space-x-1">
                      <Icon name="Users" size={12} />
                      <span>{incident?.affectedUsers?.toLocaleString()}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="Clock" size={12} />
                      <span>{incident?.duration}</span>
                    </span>
                  </div>
                  
                  <Link
                    to="/service-outage-details"
                    className="text-primary hover:text-primary/80 transition-colors"
                    onClick={(e) => e?.stopPropagation()}
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer */}
      <div className="p-4 border-t border-border">
        <div className="text-xs text-muted-foreground text-center">
          <div className="flex justify-between items-center">
            <span>Total: {processedIncidents?.length} incidents</span>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/report-outage">
                <Icon name="Plus" size={14} className="mr-1" />
                Report Issue
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentSidebar;